import { useState, useEffect, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Select, Modal, Checkbox } from 'antd';
import { ChevronLeftIcon, CheckmarkIcon } from '../../assets/icons';
import {
  useCreateLevelsBulkMutation,
  useGetLevelsQuery,
  type OrgLevel,
} from '../../features/organizations/organizationsApi';
import { useActiveOrgId } from '../../features/organizations/useActiveOrg';

/** A hand-created unit and the lower-level nodes mapped to it (mock, in-wizard). */
interface HandUnit {
  id: string;
  name: string;
  members: string[];
}
/** Mock pool of lower-level nodes to map, by what the level is made of. */
const memberPool = (from: string): string[] => {
  const n = from === 'ac' ? 8 : from === 'village' ? 14 : 24;
  const label = from === 'ac' ? 'Assembly' : from === 'village' ? 'Village' : 'Booth';
  return Array.from({ length: n }, (_, i) => `${label} ${i + 1}`);
};

const ACTIVE_RING =
  'shadow-[0px_0px_0px_4px_rgba(37,99,235,0.2),0px_4px_6px_-1px_rgba(0,74,198,0.2),0px_2px_4px_-2px_rgba(0,74,198,0.2)]';

/* ------------------------------------------------------------------ *
 * Client Admin — party hierarchy setup wizard (prototype `isParty`).
 * Four steps: Start → Levels and posts → Create units → Review.
 * Route: /client/party/setup
 *
 * The LEVELS are persisted to the org's "org"-kind hierarchy via the same
 * organisation/level/bulk/ endpoint the org-create wizard uses — this is what
 * GET /platform/client/party/ reads back. Posts, wings and the per-level unit
 * creation remain local UI (no endpoint persists those yet).
 * ------------------------------------------------------------------ */

interface WRow {
  id: string;
  name: string;
  ecl: string; // '' = own level
  from: string;
  post: string;
}
interface Starter {
  key: string;
  title: string;
  note: string;
  rows: WRow[];
}

const STARTERS: Starter[] = [
  {
    key: 'booth',
    title: 'Booth-based structure',
    note: 'The common pattern: party districts group assemblies; mandals and Shakti Kendras group booths.',
    rows: [
      { id: 'w1', name: 'State', ecl: 'state', from: '', post: 'State President' },
      { id: 'w2', name: 'Zila', ecl: '', from: 'ac', post: 'Zila President' },
      { id: 'w3', name: 'Assembly', ecl: 'ac', from: '', post: 'Assembly in-charge' },
      { id: 'w4', name: 'Mandal', ecl: '', from: 'booth', post: 'Mandal President' },
      { id: 'w5', name: 'Shakti Kendra', ecl: '', from: 'booth', post: 'Shakti Kendra Pramukh' },
      { id: 'w6', name: 'Booth', ecl: 'booth', from: '', post: 'Booth President' },
    ],
  },
  {
    key: 'village',
    title: 'Village-based mandals',
    note: 'Mandals are drawn from villages and may cross assembly or district lines. Shakti Kendras group booths inside each mandal.',
    rows: [
      { id: 'w1', name: 'State', ecl: 'state', from: '', post: 'State President' },
      { id: 'w2', name: 'Zila', ecl: '', from: 'ac', post: 'Zila President' },
      { id: 'w3', name: 'Mandal', ecl: '', from: 'village', post: 'Mandal President' },
      { id: 'w4', name: 'Shakti Kendra', ecl: '', from: 'booth', post: 'Shakti Kendra Pramukh' },
      { id: 'w5', name: 'Booth', ecl: 'booth', from: '', post: 'Booth President' },
    ],
  },
  {
    key: 'ls',
    title: 'Lok Sabha campaign',
    note: 'Organised around one seat: assemblies sit directly under the Lok Sabha constituency.',
    rows: [
      { id: 'w1', name: 'State', ecl: 'state', from: '', post: 'State in-charge' },
      { id: 'w2', name: 'Lok Sabha', ecl: 'pc', from: '', post: 'Lok Sabha in-charge' },
      { id: 'w3', name: 'Assembly', ecl: 'ac', from: '', post: 'Assembly in-charge' },
      { id: 'w4', name: 'Mandal', ecl: '', from: 'booth', post: 'Mandal in-charge' },
      { id: 'w5', name: 'Booth', ecl: 'booth', from: '', post: 'Booth agent' },
    ],
  },
  {
    key: 'import',
    title: 'Import current setup',
    note: 'Uses the levels already defined for this client (Organisation › Territory › Mandal › Shakti Kendra) and keeps their booth mapping.',
    rows: [
      { id: 'w1', name: 'Organisation', ecl: 'state', from: '', post: 'President' },
      { id: 'w2', name: 'Territory', ecl: '', from: 'ac', post: 'Territory head' },
      { id: 'w3', name: 'Mandal', ecl: '', from: 'booth', post: 'Mandal head' },
      { id: 'w4', name: 'Shakti Kendra', ecl: '', from: 'booth', post: 'Shakti Kendra head' },
      { id: 'w5', name: 'Booth', ecl: 'booth', from: '', post: 'Booth head' },
    ],
  },
  {
    key: 'blank',
    title: 'Blank',
    note: 'State and Booth only. Insert your own levels between them.',
    rows: [
      { id: 'w1', name: 'State', ecl: 'state', from: '', post: '' },
      { id: 'w2', name: 'Booth', ecl: 'booth', from: '', post: '' },
    ],
  },
];

const ECL_OPTS = [
  { value: '', label: '— Not an ECI level' },
  { value: 'state', label: 'State' },
  { value: 'district', label: 'District' },
  { value: 'pc', label: 'Lok Sabha (PC)' },
  { value: 'ac', label: 'Assembly (AC)' },
  { value: 'booth', label: 'Booth' },
];
const FROM_OPTS = [
  { value: 'ac', label: 'Assembly constituencies' },
  { value: 'booth', label: 'Booths' },
  { value: 'village', label: 'Villages (any district)' },
];
const STEPS = ['Start', 'Levels and posts', 'Create units', 'Review'];

// Read-only reference tree shown under the level editor. `key` matches ECL_OPTS values.
const ECI_TREE: { key: string; name: string; depth: number; note?: string }[] = [
  { key: 'state', name: 'State', depth: 0 },
  { key: 'district', name: 'District', depth: 1, note: 'Parallel to Lok Sabha. Both contain assemblies.' },
  { key: 'pc', name: 'Lok Sabha constituency (PC)', depth: 2 },
  { key: 'ac', name: 'Assembly constituency (AC)', depth: 3, note: 'Usually inside one district and one Lok Sabha seat.' },
  { key: 'booth', name: 'Booth (polling station / part)', depth: 4, note: 'The base unit for party coverage.' },
  { key: 'section', name: 'Section of the roll', depth: 5, note: 'A locality within a booth’s roll. Below the base unit.' },
];

const WINGS = ['Youth wing', 'Women’s wing', 'Farmers’ wing', 'SC wing', 'OBC wing', 'Minority wing'];

const MADE_OF: Record<string, string> = { ac: 'assembly constituencies', village: 'villages', booth: 'booths' };
const MADE_OF_SHORT: Record<string, string> = { ac: 'assemblies', village: 'villages', booth: 'booths' };
// ECI level name shown in the review's anchor chip, and illustrative anchored unit counts.
const ANCHOR_NAME: Record<string, string> = { state: 'State', district: 'District', pc: 'Lok Sabha (PC)', ac: 'Assembly (AC)', booth: 'Polling station', section: 'Section of roll' };
const ANCHOR_UNITS: Record<string, number> = { state: 1, district: 33, pc: 1, ac: 8, booth: 192, section: 0 };

interface UMethod { key: string; label: string; param?: 'groups' | 'boothsPer'; note: string }

// The unit-creation methods offered for a level, depending on what it is made of.
function unitMethods(from: string, parent: string): UMethod[] {
  if (from === 'ac')
    return [
      { key: 'split', label: 'Split assemblies into groups', param: 'groups', note: 'Assemblies are grouped in ECI order. Move any assembly to another group afterwards.' },
      { key: 'one', label: 'One per assembly', note: 'One unit for each assembly constituency.' },
      { key: 'manual', label: 'I’ll create them', note: 'Add the units yourself in the units editor.' },
      { key: 'upload', label: 'Upload a file', note: 'Import units and their assemblies from a spreadsheet.' },
    ];
  if (from === 'village')
    return [
      { key: 'pairs', label: 'Pairs of neighbouring tehsils', note: 'Each unit takes the villages of two neighbouring tehsils, even across a district line. Its booths come from the booth-to-village links.' },
      { key: 'one', label: 'One per tehsil', note: 'One unit for each tehsil’s villages.' },
      { key: 'manual', label: 'I’ll create them', note: 'Add the units yourself in the units editor.' },
      { key: 'upload', label: 'Upload a file', note: 'Import units and their villages from a spreadsheet.' },
    ];
  return [
    { key: 'splitParent', label: `Split each ${parent} into equal parts`, note: `Each ${parent} is split into equal-sized groups of booths.` },
    { key: 'groups', label: 'Groups of consecutive booths', param: 'boothsPer', note: `Consecutive booths within each ${parent}, in ECI part order.` },
    { key: 'manual', label: 'I’ll create them', note: 'Add the units yourself in the units editor.' },
    { key: 'upload', label: 'Upload a file', note: 'Import units and their booths from a spreadsheet.' },
  ];
}

export default function ClientPartyWizardPage() {
  const navigate = useNavigate();
  const orgId = useActiveOrgId();
  const { data: savedLevels } = useGetLevelsQuery(
    { organization: orgId as number },
    { skip: !orgId },
  );
  const [createLevels, { isLoading: saving }] = useCreateLevelsBulkMutation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const [step, setStep] = useState(1);
  const [starter, setStarter] = useState('booth');
  const [rows, setRows] = useState<WRow[]>(STARTERS[0].rows.map((r) => ({ ...r })));
  const [wings, setWings] = useState<string[]>([]);

  // When the org already has a saved party hierarchy, prefill the level rows from it
  // (once) so "Open in builder" edits the real structure instead of overwriting it with
  // the default starter on the replace-save. Posts/from/anchor aren't stored, so default.
  useEffect(() => {
    if (prefilled || !savedLevels || savedLevels.length === 0) return;
    const byId = new Map(savedLevels.map((l) => [l.id, l] as const));
    const depthOf = (l: OrgLevel) => {
      let d = 0;
      let p = l.parent;
      const seen = new Set<number>();
      while (p != null && byId.has(p) && !seen.has(p)) {
        seen.add(p);
        d += 1;
        p = byId.get(p)!.parent;
      }
      return d;
    };
    const ordered = savedLevels.slice().sort((a, b) => depthOf(a) - depthOf(b));
    setRows(ordered.map((l) => ({ id: `s${l.id}`, name: l.level_name, ecl: '', from: 'booth', post: '' })));
    setStarter('');
    setPrefilled(true);
  }, [savedLevels, prefilled]);

  // Persist the level rows (name + linear parent) to the org hierarchy, then return.
  const saveHierarchy = async () => {
    if (!orgId) {
      setSaveError('No organisation in context — reload and try again.');
      return;
    }
    setSaveError(null);
    const levels = rows.map((r, i) => ({
      id: r.id,
      name: r.name.trim() || 'Level',
      parentId: i > 0 ? rows[i - 1].id : null,
    }));
    try {
      await createLevels({ organization: orgId, levels, replace: true }).unwrap();
      navigate('/client/party');
    } catch (e) {
      const detail = (e as { data?: { detail?: string } })?.data?.detail;
      setSaveError(detail || 'Could not save the party hierarchy.');
    }
  };
  // Per own-level unit-creation choice: { mode, n } keyed by row id.
  const [unitCfg, setUnitCfg] = useState<Record<string, { mode: string; n: number }>>({});
  // "Upload a file" import panel (per own-level), mirroring the units-editor uploader:
  // which panels are open, the last result, and any read error. In-wizard mock — the
  // file is parsed client-side for a representative count; nothing is persisted here.
  const [uploadOpen, setUploadOpen] = useState<Set<string>>(new Set());
  const [uploadInfo, setUploadInfo] = useState<Record<string, { fileName: string; rows: number; units: number } | undefined>>({});
  const [uploadErr, setUploadErr] = useState<Record<string, string>>({});
  const toggleUpload = (id: string) =>
    setUploadOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const downloadUnitTemplate = (r: WRow) => {
    const madeOf = MADE_OF_SHORT[r.from] ?? 'booths';
    const sample = [
      'unit_name,member_type,member',
      `${r.name} 1,${r.from === 'ac' ? 'assembly' : r.from === 'village' ? 'village' : 'booth'},Example ${madeOf.replace(/s$/, '')} A`,
      `${r.name} 1,${r.from === 'ac' ? 'assembly' : r.from === 'village' ? 'village' : 'booth'},Example ${madeOf.replace(/s$/, '')} B`,
      `${r.name} 2,${r.from === 'ac' ? 'assembly' : r.from === 'village' ? 'village' : 'booth'},Example ${madeOf.replace(/s$/, '')} C`,
    ].join('\n');
    const url = URL.createObjectURL(new Blob([sample], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(r.name.trim() || 'units').replace(/\s+/g, '-').toLowerCase()}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const onUnitFile = (r: WRow, e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadErr((m) => ({ ...m, [r.id]: '' }));
    setUploadInfo((m) => ({ ...m, [r.id]: undefined }));
    // CSV is parsed for real counts; .xlsx is binary (parsed later in the units editor).
    if (file.name.toLowerCase().endsWith('.csv')) {
      const reader = new FileReader();
      reader.onload = () => {
        const lines = String(reader.result ?? '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        const body = lines.slice(1);
        if (body.length === 0) {
          setUploadErr((m) => ({ ...m, [r.id]: 'No rows found in the file.' }));
          return;
        }
        const units = new Set(body.map((l) => l.split(',')[0]?.trim()).filter(Boolean));
        setUploadInfo((m) => ({ ...m, [r.id]: { fileName: file.name, rows: body.length, units: units.size } }));
      };
      reader.onerror = () => setUploadErr((m) => ({ ...m, [r.id]: 'Could not read the file.' }));
      reader.readAsText(file);
    } else {
      setUploadInfo((m) => ({ ...m, [r.id]: { fileName: file.name, rows: 0, units: 0 } }));
    }
  };
  // Hand-created units editor (opened from "created by hand"): which level, its units, selection.
  const [editLevel, setEditLevel] = useState<WRow | null>(null);
  const [handUnits, setHandUnits] = useState<Record<string, HandUnit[]>>({});
  const [handSel, setHandSel] = useState<string | null>(null);
  const [handName, setHandName] = useState('');
  // Member chooser filters (mirror the platform-admin units editor).
  const [handSearch, setHandSearch] = useState('');
  const [handOnlyFree, setHandOnlyFree] = useState(false);
  const levelUnits = (id: string) => handUnits[id] ?? [];
  const addHandUnit = () => {
    if (!editLevel || !handName.trim()) return;
    const u: HandUnit = { id: `h${Date.now()}`, name: handName.trim(), members: [] };
    setHandUnits((m) => ({ ...m, [editLevel.id]: [...levelUnits(editLevel.id), u] }));
    setHandSel(u.id);
    setHandName('');
  };
  const patchHandUnit = (uid: string, patch: Partial<HandUnit>) => {
    if (!editLevel) return;
    setHandUnits((m) => ({
      ...m,
      [editLevel.id]: levelUnits(editLevel.id).map((u) => (u.id === uid ? { ...u, ...patch } : u)),
    }));
  };
  const delHandUnit = (uid: string) => {
    if (!editLevel) return;
    setHandUnits((m) => ({ ...m, [editLevel.id]: levelUnits(editLevel.id).filter((u) => u.id !== uid) }));
    if (handSel === uid) setHandSel(null);
  };
  const toggleMember = (uid: string, node: string) => {
    const u = levelUnits(editLevel?.id ?? '').find((x) => x.id === uid);
    if (!u) return;
    patchHandUnit(uid, {
      members: u.members.includes(node) ? u.members.filter((x) => x !== node) : [...u.members, node],
    });
  };

  const pickStarter = (s: Starter) => {
    setStarter(s.key);
    setRows(s.rows.map((r) => ({ ...r })));
  };
  const upd = (id: string, patch: Partial<WRow>) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const insertBelow = (id: string) =>
    setRows((rs) => {
      const i = rs.findIndex((r) => r.id === id);
      const nr: WRow = { id: `n${Date.now()}`, name: 'New level', ecl: '', from: 'booth', post: '' };
      return [...rs.slice(0, i + 1), nr, ...rs.slice(i + 1)];
    });
  const LABEL = 'text-xs font-semibold text-ink-secondary';

  // Stepper matching the org-wizard design: numbered circles + connector + status.
  const stepper = (
    <div className="overflow-x-auto pb-1">
      <div className="relative flex min-w-[600px] items-start justify-between gap-5 px-2 pt-1">
        <div className="absolute left-[72px] right-[72px] top-7 h-0.5 rounded-full bg-line/30" />
        {STEPS.map((title, i) => {
          const n = i + 1;
          const active = n === step;
          const completed = n < step;
          const pending = n > step;
          return (
            <button
              key={title}
              type="button"
              onClick={() => !pending && setStep(n)}
              disabled={pending}
              className={[
                'relative z-[1] flex w-[130px] flex-col items-center',
                pending ? 'cursor-default opacity-70' : 'cursor-pointer',
              ].join(' ')}
            >
              <div
                className={[
                  'flex size-14 items-center justify-center rounded-full text-lg font-bold',
                  active
                    ? `bg-brand text-white ${ACTIVE_RING}`
                    : completed
                      ? 'bg-brand text-white'
                      : 'bg-[#d1e2ff] text-brand',
                ].join(' ')}
              >
                {completed ? <CheckmarkIcon className="size-7" /> : n}
              </div>
              <div className="flex flex-col items-center gap-0.5 pt-3 text-center">
                <p className={active ? 'text-[15px] font-medium text-ink' : completed ? 'text-sm font-medium text-ink' : 'text-sm text-ink-secondary'}>
                  {title}
                </p>
                <p className={['text-xs font-medium', pending ? 'text-ink-secondary' : 'text-ink'].join(' ')}>
                  {active ? 'In Progress' : completed ? 'Completed' : 'Pending'}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
  const nextBtn = (label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="h-11 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
    >
      {label}
    </button>
  );
  const backBtn = (
    <button
      type="button"
      onClick={() => setStep((s) => s - 1)}
      className="h-11 rounded-lg border border-line bg-white px-4 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
    >
      Back
    </button>
  );

  const ownLevels = rows.filter((r) => !r.ecl);

  const parentOf = (r: WRow) => {
    const i = rows.findIndex((x) => x.id === r.id);
    return i > 0 ? rows[i - 1].name : 'parent';
  };
  const defaultMode = (from: string) => (from === 'ac' ? 'split' : from === 'village' ? 'pairs' : 'groups');
  const cfgOf = (r: WRow) => unitCfg[r.id] ?? { mode: defaultMode(r.from), n: r.from === 'ac' ? 2 : 5 };
  const setCfg = (id: string, patch: Partial<{ mode: string; n: number }>) =>
    setUnitCfg((c) => ({ ...c, [id]: { ...(c[id] ?? { mode: '', n: 0 }), ...patch } as { mode: string; n: number } }));
  // Illustrative unit counts for the mock.
  const unitCount = (r: WRow, cfg: { mode: string; n: number }) => {
    if (cfg.mode === 'split') return cfg.n;
    if (cfg.mode === 'groups') return Math.ceil(210 / Math.max(1, cfg.n));
    if (cfg.mode === 'pairs') return 9;
    if (cfg.mode === 'one') return r.from === 'ac' ? 8 : 14;
    if (cfg.mode === 'splitParent') return 16;
    return 0;
  };

  return (
    <div className="flex flex-col gap-[18px]">
      <div>
        <button
          type="button"
          onClick={() => navigate('/client/party')}
          className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-line bg-white pl-2.5 pr-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
        >
          <ChevronLeftIcon className="h-3 w-[7px]" />
          Cancel setup
        </button>
      </div>

      <section className="flex flex-col gap-3 rounded-xl border border-line/30 bg-white p-6 shadow-card">
        <div className="text-xs text-ink-secondary">PARTY HIERARCHY · CLIENT-ONLY · NEVER SHARED</div>
        <h1 className="text-2xl font-bold text-ink">Set up the party organisation</h1>
        {stepper}
      </section>

      {/* Step 1: Start */}
      {step === 1 && (
        <section className="flex flex-col gap-3.5 rounded-xl border border-line/30 bg-white p-6 shadow-card">
          <div>
            <h2 className="text-lg font-bold text-ink">How do you want to start?</h2>
            <p className="text-sm text-ink-secondary">Pick the closest structure. You can rename, add and remove levels in the next step.</p>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-2.5">
            {STARTERS.map((s) => {
              const on = starter === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => pickStarter(s)}
                  className={['flex flex-col gap-1.5 rounded-lg p-4 text-left transition-colors', on ? 'border-2 border-brand bg-brand-shade/60' : 'border border-line bg-white hover:bg-brand-bg'].join(' ')}
                >
                  <span className="text-[15px] font-bold text-ink">{s.title}</span>
                  <span className="text-xs font-semibold text-brand-dark">{s.rows.map((r) => r.name).join(' › ')}</span>
                  <span className="text-xs text-[#434655]">{s.note}</span>
                </button>
              );
            })}
          </div>
          <div className="flex justify-end">{nextBtn('Next: levels and posts', () => setStep(2))}</div>
        </section>
      )}

      {/* Step 2: Levels and posts */}
      {step === 2 && (
        <section className="flex flex-col gap-3.5 rounded-xl border border-line/30 bg-white p-6 shadow-card">
          <div>
            <h2 className="text-lg font-bold text-ink">Levels and posts</h2>
            <p className="max-w-[900px] text-sm text-ink-secondary">
              Where a level is the same as an ECI level, anchor it. Otherwise, say what its units are
              made of. Party districts don’t have to match revenue districts. The post is the party role
              at that level.
            </p>
          </div>
          <div className="overflow-x-auto">
            <div className="flex min-w-[1000px] flex-col">
              <div className="grid grid-cols-[minmax(140px,0.9fr)_minmax(200px,1.4fr)_minmax(170px,1fr)_minmax(160px,1fr)_120px] gap-2.5 border-b border-[#f3f4f6] py-1.5 text-[11px] uppercase tracking-[0.05em] text-ink-secondary">
                <span>Level</span>
                <span>Same as ECI level</span>
                <span>Otherwise made of</span>
                <span>Post at this level</span>
                <span />
              </div>
              {rows.map((r) => (
                <div key={r.id} className="grid grid-cols-[minmax(140px,0.9fr)_minmax(200px,1.4fr)_minmax(170px,1fr)_minmax(160px,1fr)_120px] items-center gap-2.5 border-b border-[#f3f4f6] py-2.5">
                  <Input value={r.name} onChange={(e) => upd(r.id, { name: e.target.value })} />
                  <div className="form-field-select">
                    <Select className="w-full" value={r.ecl} onChange={(v) => upd(r.id, { ecl: v })} options={ECL_OPTS} />
                  </div>
                  {r.ecl ? (
                    <span className="inline-flex w-fit items-center rounded-full bg-brand px-2.5 py-1 text-xs font-semibold text-white">Anchor · units come from ECI</span>
                  ) : (
                    <div className="form-field-select">
                      <Select className="w-full" value={r.from} onChange={(v) => upd(r.id, { from: v })} options={FROM_OPTS} />
                    </div>
                  )}
                  <Input value={r.post} onChange={(e) => upd(r.id, { post: e.target.value })} placeholder="e.g. President" />
                  <div className="flex gap-1.5">
                    <button type="button" onClick={() => insertBelow(r.id)} className="h-9 rounded-lg border border-brand bg-white px-2.5 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade">
                      + Below
                    </button>
                    {rows.length > 2 && (
                      <button type="button" onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))} className="h-9 rounded-lg border border-[#fecaca] bg-white px-2.5 text-xs font-semibold text-[#dc2626] transition-colors hover:bg-[#fef2f2]">
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Read-only ECI reference tree, with the party levels anchored to each. */}
          <div className="flex flex-col rounded-lg border border-line/50 bg-brand-bg">
            <div className="flex items-center justify-between border-b border-line/50 px-4 py-2.5">
              <span className="text-[13px] font-bold text-ink">The complete ECI hierarchy</span>
              <span className="text-xs text-ink-secondary">Read-only. Your levels anchored to each are shown on the right.</span>
            </div>
            {ECI_TREE.map((n) => {
              const anchored = rows.filter((r) => r.ecl === n.key);
              return (
                <div key={n.key} className="flex items-start justify-between gap-3 border-b border-line/40 px-4 py-2.5 last:border-b-0">
                  <div style={{ paddingLeft: n.depth * 18 }} className="flex flex-col gap-0.5">
                    <span className="text-[13px] font-bold text-ink">{n.name}</span>
                    {n.note && <span className="text-xs text-ink-secondary">{n.note}</span>}
                  </div>
                  {anchored.length ? (
                    <span className="flex flex-wrap justify-end gap-1">
                      {anchored.map((r) => (
                        <span key={r.id} className="inline-flex items-center rounded-full bg-brand px-2.5 py-1 text-xs font-semibold text-white">
                          ← {r.name}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="whitespace-nowrap text-xs text-ink-secondary">not used</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Wings reuse the structure with their own posts. */}
          <div className="flex flex-col gap-2">
            <span className="text-[13px] font-semibold text-ink-secondary">Wings that reuse this structure with their own posts</span>
            <div className="flex flex-wrap gap-2">
              {WINGS.map((w) => {
                const on = wings.includes(w);
                return (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setWings((ws) => (on ? ws.filter((x) => x !== w) : [...ws, w]))}
                    className={['h-10 rounded-lg px-3.5 text-[13px] font-semibold transition-colors', on ? 'border border-brand bg-brand-shade text-brand' : 'border border-line bg-white text-ink hover:bg-brand-shade'].join(' ')}
                  >
                    {w}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-between">
            {backBtn}
            {nextBtn('Next: create units', () => setStep(3))}
          </div>
        </section>
      )}

      {/* Step 3: Create units */}
      {step === 3 && (
        <section className="flex flex-col gap-3.5 rounded-xl border border-line/30 bg-white p-6 shadow-card">
          <div>
            <h2 className="text-lg font-bold text-ink">Create the units</h2>
            <p className="max-w-[900px] text-sm text-ink-secondary">
              Anchored levels need nothing: their units come from ECI. For the others, start the units
              automatically from ECI booth order, then adjust them in the units editor. Nothing here is
              final.
            </p>
          </div>
          {ownLevels.length === 0 && <p className="text-[13px] text-ink-secondary">No own levels — every level is anchored to ECI.</p>}
          {ownLevels.map((r) => {
            const methods = unitMethods(r.from, parentOf(r));
            const cfg = cfgOf(r);
            const active = methods.find((m) => m.key === cfg.mode) ?? methods[0];
            const units = unitCount(r, cfg);
            const summary =
              active.key === 'upload'
                ? 'set in the units editor'
                : `→ ${units} units · 100% of booths${r.from === 'village' ? ' · 1 span two districts' : ''}`;
            return (
              <div key={r.id} className="flex flex-col gap-3 rounded-lg border border-line/50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <span className="font-bold text-ink">
                    {r.name} <span className="text-xs font-normal text-ink-secondary">made of {MADE_OF[r.from] ?? 'booths'}</span>
                  </span>
                  {active.key === 'manual' ? (
                    <button
                      type="button"
                      onClick={() => {
                        setEditLevel(r);
                        setHandSel(null);
                      }}
                      className="whitespace-nowrap text-[13px] font-semibold text-brand transition-colors hover:text-brand-dark hover:underline"
                    >
                      → created by hand{levelUnits(r.id).length ? ` · ${levelUnits(r.id).length} units` : ''}
                    </button>
                  ) : active.key === 'upload' ? (
                    <button
                      type="button"
                      onClick={() => toggleUpload(r.id)}
                      className="whitespace-nowrap text-[13px] font-semibold text-brand transition-colors hover:text-brand-dark hover:underline"
                    >
                      {uploadInfo[r.id]
                        ? `→ ${uploadInfo[r.id]!.units || 'file'} ${uploadInfo[r.id]!.units ? 'units from file' : 'received'}`
                        : '→ set in the units editor'}
                    </button>
                  ) : (
                    <span className="whitespace-nowrap text-[13px] font-semibold text-brand">{summary}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {methods.map((m) => {
                    const on = m.key === cfg.mode;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setCfg(r.id, { mode: m.key })}
                        className={['h-10 rounded-lg px-3.5 text-[13px] font-semibold transition-colors', on ? 'border border-brand bg-brand text-white' : 'border border-line bg-white text-ink hover:bg-brand-shade'].join(' ')}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
                {active.param && (
                  <div className="flex items-center gap-2.5">
                    <span className="text-[13px] font-semibold text-ink">{active.param === 'groups' ? 'Number of groups' : 'Booths per group'}</span>
                    <div className="form-field-select w-20">
                      <Select
                        className="w-full"
                        value={cfg.n}
                        onChange={(v) => setCfg(r.id, { n: v })}
                        options={(active.param === 'groups' ? [2, 3, 4, 5, 6] : [3, 4, 5, 6, 8, 10]).map((v) => ({ value: v, label: String(v) }))}
                      />
                    </div>
                  </div>
                )}
                <span className="text-[13px] text-ink-secondary">{active.note}</span>

                {/* Upload a file — import panel mirroring the units-editor uploader. */}
                {active.key === 'upload' && uploadOpen.has(r.id) && (
                  <div className="flex flex-col gap-3 rounded-lg border border-line/50 bg-brand-bg p-4">
                    <h3 className="text-[15px] font-bold text-ink">Import {r.name} units</h3>
                    <p className="text-[13px] text-ink-secondary">
                      A CSV or Excel file, one row per member. Rows with the same{' '}
                      <b className="text-ink">unit_name</b> fold into one unit.{' '}
                      <b className="text-ink">member_type</b> is the {MADE_OF_SHORT[r.from] ?? 'booths'} this
                      level is made of, and <b className="text-ink">member</b> is matched by code first,
                      then by name. Unmatched rows are reported and not saved.
                    </p>
                    <pre className="overflow-x-auto rounded-lg bg-white p-3.5 text-[13px] leading-6 text-ink-secondary">
                      {`unit_name,member_type,member\n${r.name} 1,${MADE_OF_SHORT[r.from]?.replace(/s$/, '') ?? 'booth'},Example A\n${r.name} 1,${MADE_OF_SHORT[r.from]?.replace(/s$/, '') ?? 'booth'},Example B`}
                    </pre>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadUnitTemplate(r)}
                        className="h-11 rounded-lg border border-brand bg-white px-4 text-[13px] font-semibold text-brand transition-colors hover:bg-brand-shade"
                      >
                        Download CSV template
                      </button>
                      <label className="inline-flex h-11 cursor-pointer items-center rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark">
                        Import from Excel or CSV
                        <input type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => onUnitFile(r, e)} />
                      </label>
                    </div>
                    {uploadErr[r.id] && (
                      <div className="rounded-lg border border-[#fecaca] bg-[#fef2f2] p-3 text-[13px] font-semibold text-[#b42318]">
                        {uploadErr[r.id]}
                      </div>
                    )}
                    {uploadInfo[r.id] && (
                      <div className="rounded-lg border border-line/50 bg-white p-3.5 text-[13px] text-ink">
                        {uploadInfo[r.id]!.rows > 0 ? (
                          <span>
                            Read <b>{uploadInfo[r.id]!.fileName}</b>: {uploadInfo[r.id]!.rows} row
                            {uploadInfo[r.id]!.rows === 1 ? '' : 's'} · {uploadInfo[r.id]!.units} unit
                            {uploadInfo[r.id]!.units === 1 ? '' : 's'}. Adjust them in the units editor after
                            creating the hierarchy.
                          </span>
                        ) : (
                          <span>
                            Received <b>{uploadInfo[r.id]!.fileName}</b>. It will be parsed in the units editor
                            after creating the hierarchy.
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-between">
            {backBtn}
            {nextBtn('Next: review', () => setStep(4))}
          </div>
        </section>
      )}

      {/* Step 4: Review */}
      {step === 4 && (
        <section className="flex flex-col gap-3.5 rounded-xl border border-line/30 bg-white p-6 shadow-card">
          <div>
            <h2 className="text-lg font-bold text-ink">Review</h2>
            <p className="max-w-[900px] text-sm text-ink-secondary">
              This is what will be created. Coverage is the share of the boundary’s booths placed in a unit
              at each level.
            </p>
          </div>
          <div className="overflow-x-auto">
            <div className="flex min-w-[820px] flex-col">
              <div className="grid grid-cols-[minmax(180px,1.3fr)_minmax(220px,1.4fr)_minmax(180px,1.2fr)_80px_90px] gap-2.5 border-b border-[#f3f4f6] py-1.5 text-[11px] uppercase tracking-[0.05em] text-ink-secondary">
                <span>Level</span>
                <span>Binding</span>
                <span>Post</span>
                <span className="text-right">Units</span>
                <span className="text-right">Booths</span>
              </div>
              {rows.map((r) => {
                const units = r.ecl ? ANCHOR_UNITS[r.ecl] ?? 1 : unitCount(r, cfgOf(r));
                return (
                  <div key={r.id} className="grid grid-cols-[minmax(180px,1.3fr)_minmax(220px,1.4fr)_minmax(180px,1.2fr)_80px_90px] items-center gap-2.5 border-b border-[#f3f4f6] py-2.5">
                    <span className="flex items-center gap-2.5">
                      <span className={['block size-2.5 shrink-0 rounded-full', r.ecl ? 'bg-brand' : 'border-2 border-[#f59e0b] bg-white'].join(' ')} aria-hidden />
                      <span className="font-bold text-ink">{r.name}</span>
                    </span>
                    {r.ecl ? (
                      <span className="inline-flex w-fit items-center rounded-full bg-brand px-2.5 py-1 text-xs font-semibold text-white">
                        Anchor · ECI › {ANCHOR_NAME[r.ecl] ?? r.ecl}
                      </span>
                    ) : (
                      <span className="inline-flex w-fit items-center rounded-full bg-[#fef3e2] px-2.5 py-1 text-xs font-semibold text-[#b45309]">
                        Own · groups of {MADE_OF_SHORT[r.from] ?? 'booths'}
                      </span>
                    )}
                    <span className="text-[13px] text-ink">{r.post || '—'}</span>
                    <span className="text-right text-[13px] font-semibold text-ink">{units}</span>
                    <span className="text-right text-[13px] font-semibold text-brand">100%</span>
                  </div>
                );
              })}
            </div>
          </div>
          <span className="text-[13px] text-ink-secondary">
            {wings.length ? `${wings.join(', ')} reuse this structure with their own posts.` : 'No wings selected. They can be added later.'}
          </span>
          <p className="border-l-2 border-line pl-3 text-[13px] text-ink-secondary">
            After creating it, open any own level to adjust units in the units editor, then assign people to
            posts from the Party Persons directory.
          </p>
          {saveError && (
            <div className="rounded-md border border-[#e9a3a3] bg-[#fdecec] px-3 py-2 text-[13px] text-[#7f1d1d]">
              {saveError}
            </div>
          )}
          <div className="flex justify-between">
            {backBtn}
            <button
              type="button"
              disabled={saving}
              onClick={saveHierarchy}
              className="h-11 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Creating…' : 'Create party hierarchy'}
            </button>
          </div>
        </section>
      )}

      {/* Hand-created units editor — add units + map the next-lower-level nodes (like the
          department edit-units screen). Mock, in-wizard. */}
      <Modal
        open={!!editLevel}
        onCancel={() => setEditLevel(null)}
        title={editLevel ? `Units of ${editLevel.name}` : 'Units'}
        width={920}
        footer={
          <button
            type="button"
            onClick={() => setEditLevel(null)}
            className="h-10 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            Done
          </button>
        }
      >
        {editLevel &&
          (() => {
            const madeOf = MADE_OF_SHORT[editLevel.from] ?? 'booths';
            const pool = memberPool(editLevel.from);
            const list = levelUnits(editLevel.id);
            const sel = list.find((u) => u.id === handSel) ?? null;
            return (
              <div className="flex flex-col gap-3 py-1 lg:flex-row">
                {/* Unit list */}
                <div className="flex w-full shrink-0 flex-col gap-2.5 lg:w-[280px]">
                  <span className="text-xs font-semibold text-ink-secondary">
                    Each unit is made of {madeOf}. Add units, then map their {madeOf}.
                  </span>
                  <div className="flex gap-2">
                    <Input
                      value={handName}
                      onChange={(e) => setHandName(e.target.value)}
                      onPressEnter={addHandUnit}
                      placeholder={`New ${editLevel.name} name`}
                    />
                    <button
                      type="button"
                      onClick={addHandUnit}
                      disabled={!handName.trim()}
                      className="h-9 shrink-0 rounded-lg bg-brand px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      + Add
                    </button>
                  </div>
                  <div className="flex max-h-[360px] flex-col overflow-y-auto">
                    {list.length === 0 && (
                      <span className="border-t border-[#f3f4f6] pt-2.5 text-[13px] text-ink-secondary">
                        No units yet. Add one above.
                      </span>
                    )}
                    {list.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setHandSel(u.id)}
                        className={[
                          'flex flex-col items-start gap-0.5 border-t border-[#f3f4f6] px-2 py-2.5 text-left transition-colors first:border-t-0',
                          u.id === handSel ? 'rounded-lg bg-brand-shade' : 'hover:bg-brand-bg',
                        ].join(' ')}
                      >
                        <span className="text-sm font-semibold text-ink">{u.name}</span>
                        <span className="text-xs text-ink-secondary">{u.members.length} {madeOf}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Member chooser */}
                <div className="min-w-0 flex-1 rounded-lg border border-line/50 p-4">
                  {!sel ? (
                    <p className="text-sm text-ink-secondary">Add a unit, or select one to map its {madeOf}.</p>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <label className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="text-xs font-semibold text-ink-secondary">Unit name</span>
                          <Input value={sel.name} onChange={(e) => patchHandUnit(sel.id, { name: e.target.value })} />
                        </label>
                        <button
                          type="button"
                          onClick={() => delHandUnit(sel.id)}
                          className="mt-[22px] h-9 shrink-0 rounded-lg border border-[#fecaca] bg-white px-3 text-xs font-semibold text-[#dc2626] transition-colors hover:bg-[#fef2f2]"
                        >
                          Delete
                        </button>
                      </div>
                      <span className="text-sm font-bold text-ink">
                        Members · {sel.members.length} {madeOf}
                      </span>
                      {(() => {
                        // Nodes already taken by OTHER units of this level; the current unit keeps its own.
                        const takenByOthers = new Set(
                          list.filter((u) => u.id !== sel.id).flatMap((u) => u.members),
                        );
                        const q = handSearch.trim().toLowerCase();
                        const shown = pool.filter((node) => {
                          if (q && !node.toLowerCase().includes(q)) return false;
                          if (handOnlyFree && takenByOthers.has(node) && !sel.members.includes(node)) return false;
                          return true;
                        });
                        const allShownOn = shown.length > 0 && shown.every((n) => sel.members.includes(n));
                        const someShownOn = shown.some((n) => sel.members.includes(n));
                        const toggleAllShown = () =>
                          patchHandUnit(sel.id, {
                            members: allShownOn
                              ? sel.members.filter((m) => !shown.includes(m))
                              : Array.from(new Set([...sel.members, ...shown])),
                          });
                        return (
                          <>
                            <div className="flex flex-wrap items-center gap-3">
                              <Input
                                className="min-w-[200px] flex-1"
                                value={handSearch}
                                onChange={(e) => setHandSearch(e.target.value)}
                                placeholder={`Search ${madeOf} by name`}
                                allowClear
                              />
                              <label className="flex shrink-0 cursor-pointer items-center gap-2 text-[13px] text-ink">
                                <Checkbox checked={handOnlyFree} onChange={(e) => setHandOnlyFree(e.target.checked)} />
                                Only show ones not in another unit
                              </label>
                            </div>
                            <div className="flex flex-col overflow-hidden rounded-lg border border-line/50">
                              <div className="flex items-center justify-between gap-2 border-b border-[#f3f4f6] bg-[#f9fafb] px-3 py-2">
                                <span className="text-xs font-semibold text-ink-secondary">
                                  {madeOf} · {shown.length} shown
                                </span>
                                <label className="flex cursor-pointer items-center gap-2 text-[13px] text-ink">
                                  <Checkbox
                                    checked={allShownOn}
                                    indeterminate={!allShownOn && someShownOn}
                                    onChange={toggleAllShown}
                                    disabled={shown.length === 0}
                                  />
                                  Select all shown
                                </label>
                              </div>
                              <div className="max-h-[320px] overflow-y-auto p-2">
                                {shown.length === 0 ? (
                                  <span className="block p-2 text-[13px] text-ink-secondary">
                                    {q || handOnlyFree ? `No ${madeOf} match the filters.` : `No ${madeOf} available.`}
                                  </span>
                                ) : (
                                  <div className="grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2">
                                    {shown.map((node) => {
                                      const on = sel.members.includes(node);
                                      return (
                                        <label
                                          key={node}
                                          className={['flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-ink transition-colors', on ? 'bg-brand-shade/50' : 'hover:bg-brand-bg'].join(' ')}
                                        >
                                          <Checkbox checked={on} onChange={() => toggleMember(sel.id, node)} />
                                          <span className="truncate font-medium">{node}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
      </Modal>
    </div>
  );
}
